import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  TrendingUp,
  Package,
  Tag,
  ShoppingBag,
  AlertCircle,
  Plus,
  ExternalLink,
  Eye,
  Upload,
  RefreshCw
} from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';

const Competitors = () => {
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Competitors - Mappy';
    fetchCompetitors();
  }, []);

  const fetchCompetitors = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/tracking/competitors/');
      // Handle paginated response (results) or direct array
      let competitorsData = response.data.results || response.data;
      
      // Sort by updated_at (most recent first)
      competitorsData = competitorsData.sort((a, b) => {
        const dateA = new Date(a.updated_at);
        const dateB = new Date(b.updated_at);
        return dateB - dateA; // Descending order (newest first)
      });
      
      setCompetitors(competitorsData);

      // Fetch stats for each competitor
      const statsPromises = competitorsData.map(comp =>
        axiosInstance.get(`/api/tracking/competitors/${comp.id}/stats/`)
      );
      const statsResponses = await Promise.all(statsPromises);
      const statsMap = {};
      statsResponses.forEach((res, index) => {
        statsMap[competitorsData[index].id] = res.data;
      });
      setStats(statsMap);
    } catch (error) {
      console.error('Error fetching competitors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCompetitor = (competitorId) => {
    navigate(`/competitors/${competitorId}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">Loading competitors...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Mappy</h1>
            <p className="text-muted-foreground mt-1">
              Track your competitors' products, brands, and categories
            </p>
          </div>
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add Competitor
          </Button>
        </div>

        <Separator />

        {competitors.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No competitors yet</h3>
              <p className="text-muted-foreground text-center mb-4">
                Start tracking your competitors by adding your first one.
              </p>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Your First Competitor
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="border rounded-md bg-card">
            {/* Header */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-muted/40 font-medium text-xs text-muted-foreground border-b uppercase tracking-wider">
              <div className="col-span-3">Competitor</div>
              <div className="col-span-2">Sync Method</div>
              <div className="col-span-3">Inventory Stats</div>
              <div className="col-span-2">Last Update</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>

            <div className="divide-y">
              {competitors.map((competitor) => {
                const competitorStats = stats[competitor.id] || {};
                const hasNewChanges = competitor.new_changes_count > 0;
                const manualUploadCompetitors = ['Finnish Design Shop', 'Made in Design'];
                const requiresManualUpload = manualUploadCompetitors.includes(competitor.name);
                const supportsBoth = competitor.name === 'No-GA';

                return (
                  <div key={competitor.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-muted/5 transition-colors group">
                    {/* Competitor Name & Website */}
                    <div className="md:col-span-3 space-y-1">
                      <div className="flex items-center gap-3">
                        {/* Logo */}
                        {competitor.image_url ? (
                          <img
                            src={competitor.image_url}
                            alt={competitor.name}
                            className="h-6 w-6 rounded object-contain bg-white border flex-shrink-0"
                          />
                        ) : (
                          <div className="h-6 w-6 rounded bg-muted flex items-center justify-center text-muted-foreground text-[10px] font-medium flex-shrink-0">
                            {competitor.name.substring(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-base">{competitor.name}</h3>
                            {hasNewChanges && (
                              <Badge variant="outline" className="h-5 px-1.5 text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                                {competitor.new_changes_count} new
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      {competitor.website && (
                        <a
                          href={competitor.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors truncate"
                        >
                          {competitor.website.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>

                    {/* Sync Method */}
                    <div className="md:col-span-2 flex items-center gap-2">
                      {requiresManualUpload ? (
                        <div className="flex items-center gap-1.5 text-sm text-orange-600 bg-orange-50 px-2 py-1 rounded border border-orange-100">
                          <Upload className="h-3.5 w-3.5" />
                          <span>Manual</span>
                        </div>
                      ) : supportsBoth ? (
                        <div className="flex items-center gap-1.5 text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded border border-blue-100">
                          <div className="flex gap-1">
                            <RefreshCw className="h-3.5 w-3.5" />
                            <Upload className="h-3.5 w-3.5" />
                          </div>
                          <span>Hybrid</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-sm text-green-600 bg-green-50 px-2 py-1 rounded border border-green-100">
                          <RefreshCw className="h-3.5 w-3.5" />
                          <span>Auto</span>
                        </div>
                      )}
                    </div>

                    {/* Inventory Stats */}
                    <div className="md:col-span-3 grid grid-cols-3 gap-2 text-sm">
                      {/* Categories */}
                      <div
                        className="flex flex-col cursor-pointer hover:bg-muted/10 rounded-sm p-1 -m-1 transition-colors"
                        onClick={() => navigate(`/competitors/${competitor.id}?tab=categories`)}
                        title="View Categories"
                      >
                        <div className="flex items-center gap-1.5 text-muted-foreground mb-0.5">
                          <Tag className="h-3.5 w-3.5" />
                          <span className="text-xs uppercase">Cats</span>
                        </div>
                        <div className="font-medium">
                          {competitor.categories_count || 0}
                          {(competitorStats.recent_categories > 0 || competitorStats.recent_categories_removed > 0) && (
                            <span className="text-xs ml-1 opacity-80">
                              {competitorStats.recent_categories > 0 && <span className="text-green-600">+{competitorStats.recent_categories}</span>}
                              {competitorStats.recent_categories_removed > 0 && <span className="text-red-600">-{competitorStats.recent_categories_removed}</span>}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Brands */}
                      <div
                        className="flex flex-col cursor-pointer hover:bg-muted/10 rounded-sm p-1 -m-1 transition-colors"
                        onClick={() => navigate(`/competitors/${competitor.id}?tab=brands`)}
                        title="View Brands"
                      >
                        <div className="flex items-center gap-1.5 text-muted-foreground mb-0.5">
                          <ShoppingBag className="h-3.5 w-3.5" />
                          <span className="text-xs uppercase">Brands</span>
                        </div>
                        <div className="font-medium">
                          {competitor.brands_count || 0}
                          {(competitorStats.recent_brands > 0 || competitorStats.recent_brands_removed > 0) && (
                            <span className="text-xs ml-1 opacity-80">
                              {competitorStats.recent_brands > 0 && <span className="text-green-600">+{competitorStats.recent_brands}</span>}
                              {competitorStats.recent_brands_removed > 0 && <span className="text-red-600">-{competitorStats.recent_brands_removed}</span>}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Products */}
                      <div
                        className="flex flex-col cursor-pointer hover:bg-muted/10 rounded-sm p-1 -m-1 transition-colors"
                        onClick={() => navigate(`/competitors/${competitor.id}?tab=products`)}
                        title="View Products"
                      >
                        <div className="flex items-center gap-1.5 text-muted-foreground mb-0.5">
                          <Package className="h-3.5 w-3.5" />
                          <span className="text-xs uppercase">Prods</span>
                        </div>
                        <div className="font-medium">
                          {competitor.products_count || 0}
                          {(competitorStats.recent_products > 0 || competitorStats.recent_products_removed > 0) && (
                            <span className="text-xs ml-1 opacity-80">
                              {competitorStats.recent_products > 0 && <span className="text-green-600">+{competitorStats.recent_products}</span>}
                              {competitorStats.recent_products_removed > 0 && <span className="text-red-600">-{competitorStats.recent_products_removed}</span>}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Last Update */}
                    <div className="md:col-span-2 text-sm">
                      <div className="font-medium text-muted-foreground">
                        {new Date(competitor.updated_at).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-muted-foreground/60">
                        {new Date(competitor.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="md:col-span-2 flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-primary"
                        onClick={() => handleViewCompetitor(competitor.id)}
                      >
                        View Details
                        <ExternalLink className="ml-2 h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Competitors;

